import React, { useEffect, useRef, useState } from "react";
import {
  Drawer,
  IconButton,
  TextField,
  Typography,
  Button,
  Chip,
  Tooltip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import ReplayIcon from "@mui/icons-material/Replay";
import ScienceIcon from "@mui/icons-material/Science";

/**
 * Simulador de flujos — recorre el grafo del editor en el navegador, sin
 * enviar nada por WhatsApp. Muestra la conversación tal como la vería el
 * contacto y resalta en el lienzo el nodo que se está ejecutando.
 *
 * Imita la lógica de ActionsWebhookService: menús por número o texto,
 * condiciones con variables, input que guarda variables, aleatorizador, etc.
 * Las acciones con efecto real (HTTP, etiquetas, colas, IA) solo se anuncian.
 */

const MAX_STEPS = 200;
const STEP_DELAY = 450;
const CONTACT_NAME = "Cliente de prueba";

const useStyles = makeStyles(theme => ({
  paper: {
    width: 400,
    maxWidth: "100vw",
    display: "flex",
    flexDirection: "column",
    zIndex: 1500
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "12px 12px 12px 16px",
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  hint: {
    padding: "8px 16px",
    fontSize: 12,
    color: theme.palette.text.secondary,
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  chat: {
    flex: 1,
    overflowY: "auto",
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    background: theme.palette.type === "dark" ? "#0b141a" : "#efeae2"
  },
  bubble: {
    maxWidth: "85%",
    padding: "8px 10px",
    borderRadius: 8,
    fontSize: 13,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    boxShadow: "0 1px 1px rgba(0,0,0,0.12)"
  },
  bot: {
    alignSelf: "flex-start",
    background: theme.palette.type === "dark" ? "#202c33" : "#fff",
    color: theme.palette.text.primary
  },
  user: {
    alignSelf: "flex-end",
    background: theme.palette.type === "dark" ? "#005c4b" : "#d9fdd3",
    color: theme.palette.text.primary
  },
  system: {
    alignSelf: "center",
    maxWidth: "95%",
    fontSize: 11,
    padding: "3px 10px",
    borderRadius: 10,
    background: theme.palette.type === "dark" ? "#1f2937" : "#e1f3fb",
    color: theme.palette.text.secondary,
    textAlign: "center"
  },
  warn: {
    background: theme.palette.type === "dark" ? "#422006" : "#fff4e5",
    color: theme.palette.type === "dark" ? "#fdba74" : "#8a4b00"
  },
  options: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 6
  },
  footer: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    padding: 12,
    borderTop: `1px solid ${theme.palette.divider}`
  },
  vars: {
    padding: "6px 16px",
    fontSize: 11,
    color: theme.palette.text.secondary,
    borderTop: `1px solid ${theme.palette.divider}`,
    maxHeight: 80,
    overflowY: "auto"
  }
}));

const sleep = ms => new Promise(r => setTimeout(r, ms));

const sanitize = s =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();

const interpolate = (text, vars) =>
  String(text ?? "")
    .replace(/\$\{([^}]+)\}/g, (m, k) => (vars[k.trim()] !== undefined ? vars[k.trim()] : m))
    .replace(/\{\{\s*([^}]+?)\s*\}\}/g, (m, k) => {
      if (vars[k] !== undefined) return vars[k];
      if (["name", "nome", "nombre", "firstName"].includes(k)) return CONTACT_NAME;
      return m;
    });

// Valor de un operando: admite ${var}, {{var}} o el nombre de la variable a secas.
const resolveOperand = (raw, vars) => {
  const s = String(raw ?? "").trim();
  if (vars[s] !== undefined) return String(vars[s]);
  return interpolate(s, vars);
};

const compare = (left, operator, right) => {
  const l = sanitize(left);
  const r = sanitize(right);
  const ln = parseFloat(String(left).replace(",", "."));
  const rn = parseFloat(String(right).replace(",", "."));
  switch (operator) {
    case "equals": return l === r;
    case "notEquals": return l !== r;
    case "contains": return l.includes(r);
    case "notContains": return !l.includes(r);
    case "containsAny": return r.split(",").map(x => x.trim()).filter(Boolean).some(x => l.includes(x));
    case "startsWith": return l.startsWith(r);
    case "endsWith": return l.endsWith(r);
    case "greaterThan": return ln > rn;
    case "lessThan": return ln < rn;
    case "greaterOrEqual": return ln >= rn;
    case "lessOrEqual": return ln <= rn;
    case "isEmpty": return l === "";
    case "isNotEmpty": return l !== "";
    case "regex":
      try { return new RegExp(right, "i").test(left); } catch { return false; }
    default: return false;
  }
};

const MEDIA_LABEL = {
  img: "🖼️ Imagen",
  audio: "🎵 Audio",
  video: "🎬 Video",
  document: "📄 Documento"
};

const FlowSimulatorDrawer = ({ open, onClose, nodes, edges, onActiveNode }) => {
  const classes = useStyles();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [waiting, setWaiting] = useState(null); // { node }
  const [running, setRunning] = useState(false);
  const [vars, setVars] = useState({});
  const runIdRef = useRef(0);
  const varsRef = useRef({});
  const chatRef = useRef(null);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  const reset = () => {
    runIdRef.current += 1;
    varsRef.current = {};
    setVars({});
    setMessages([]);
    setWaiting(null);
    setRunning(false);
    setInput("");
    onActiveNode && onActiveNode(null);
  };

  useEffect(() => {
    if (!open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const push = msg => setMessages(m => [...m, msg]);
  const setVar = (k, v) => {
    varsRef.current = { ...varsRef.current, [k]: v };
    setVars(varsRef.current);
  };

  const nodeById = id => nodes.find(n => n.id === id);
  const nextOf = (nodeId, handle) => {
    const out = edges.filter(e => e.source === nodeId);
    const edge = handle ? out.find(e => e.sourceHandle === handle) : out[0];
    return edge ? nodeById(edge.target) : null;
  };

  // Ejecuta nodos desde `node` hasta que uno pida respuesta o el flujo termine.
  const runFrom = async node => {
    const runId = runIdRef.current;
    const alive = () => runId === runIdRef.current;
    setRunning(true);
    setWaiting(null);
    let steps = 0;
    let current = node;

    while (current && alive()) {
      if (++steps > MAX_STEPS) {
        push({ from: "system", warn: true, text: `Se detuvo tras ${MAX_STEPS} pasos: posible bucle infinito.` });
        break;
      }
      onActiveNode && onActiveNode(current.id);
      await sleep(STEP_DELAY);
      if (!alive()) return;

      const d = current.data || {};
      const v = varsRef.current;
      let next = undefined; // undefined = seguir por la primera salida

      switch (current.type) {
        case "start":
          push({ from: "system", text: "▶ Inicio del flujo" });
          break;

        case "message":
          push({ from: "bot", text: interpolate(d.label, v) });
          break;

        case "img":
        case "audio":
        case "video":
          push({ from: "bot", text: `${MEDIA_LABEL[current.type]}${d.caption ? `\n${interpolate(d.caption, v)}` : ""}` });
          break;

        case "interval":
          push({ from: "system", text: `⏱ Espera de ${d.sec || 0} s (en la simulación es instantánea)` });
          break;

        case "singleBlock":
          for (const key of d.seq || []) {
            const el = (d.elements || []).find(e => e.number === key);
            if (!el) continue;
            if (el.type === "message") push({ from: "bot", text: interpolate(el.value, v) });
            else if (el.type === "interval") push({ from: "system", text: `⏱ Espera de ${el.value} s` });
            else push({ from: "bot", text: MEDIA_LABEL[el.type] || "📎 Archivo" });
          }
          break;

        case "menu":
        case "interactiveMenu": {
          const opts = d.arrayOption || [];
          const text = current.type === "menu"
            ? `${interpolate(d.message, v)}\n\n${opts.map(o => `[${o.number}] ${o.value}`).join("\n")}`
            : `${interpolate(d.message, v)}${d.footer ? `\n_${d.footer}_` : ""}`;
          push({ from: "bot", text, options: opts });
          setWaiting({ node: current });
          setRunning(false);
          return;
        }

        case "input":
          if (d.variableName && v[d.variableName] !== undefined && !d.alwaysAsk) {
            push({ from: "system", text: `La variable "${d.variableName}" ya existe: se omite la pregunta` });
            break;
          }
          push({ from: "bot", text: interpolate(d.question, v) });
          setWaiting({ node: current });
          setRunning(false);
          return;

        case "randomizer": {
          const pct = Number(d.percent) || 0;
          const pickA = Math.random() * 100 < pct;
          push({ from: "system", text: `🎲 Aleatorizador: salió la rama ${pickA ? `${pct}%` : `${100 - pct}%`}` });
          next = nextOf(current.id, pickA ? "a" : "b");
          break;
        }

        case "conditionCompare": {
          const conds = d.conditions && d.conditions.length
            ? d.conditions
            : [{ leftValue: d.leftValue, operator: d.operator || "equals", rightValue: d.rightValue }];
          const results = conds.map(c => compare(resolveOperand(c.leftValue, v), c.operator, resolveOperand(c.rightValue, v)));
          const ok = (d.logicOperator || "AND") === "OR" ? results.some(Boolean) : results.every(Boolean);
          push({ from: "system", text: `🔀 Condición: ${ok ? "VERDADERO" : "FALSO"}` });
          next = nextOf(current.id, ok ? "true" : "false");
          break;
        }

        case "variable":
          if (d.variableName) {
            const val = d.isExpression ? `(expresión: ${d.variableExpression})` : interpolate(d.variableValue, v);
            setVar(d.variableName, val);
            push({ from: "system", text: `📝 Variable "${d.variableName}" = ${val}` });
          }
          break;

        case "ticket":
          push({ from: "system", text: `📥 Se transfiere a la cola "${d.queue?.name || "?"}"` });
          break;

        case "attendant":
          push({ from: "system", text: `👤 Se asigna al agente "${d.user?.name || "?"}"` });
          break;

        case "tag":
          push({ from: "system", text: `🏷️ Se agrega la etiqueta "${d.tag?.name || "?"}"` });
          break;

        case "removeTag":
          push({ from: "system", text: `🏷️ Se quita la etiqueta "${d.tag?.name || d.tag || "?"}"` });
          break;

        case "httpRequest":
          push({ from: "system", warn: true, text: `🌐 Petición ${d.method || "POST"} ${d.url || ""} — no se ejecuta en la simulación` });
          break;

        case "switchFlow":
          push({ from: "system", text: "↪ Cambia a otro flujo. La simulación termina aquí." });
          onActiveNode && onActiveNode(current.id);
          setRunning(false);
          return;

        case "aiAgent":
          push({ from: "system", text: "🤖 Un agente de IA toma la conversación. La simulación termina aquí." });
          setRunning(false);
          return;

        default:
          push({ from: "system", text: `Nodo "${current.type}" (no simulado, se continúa)` });
      }

      current = next === undefined ? nextOf(current.id) : next;
      if (!current) {
        push({ from: "system", text: "■ Fin del flujo" });
        onActiveNode && onActiveNode(null);
      }
    }
    if (alive()) setRunning(false);
  };

  const answer = async text => {
    const reply = text.trim();
    if (!reply || running) return;
    setInput("");
    push({ from: "user", text: reply });

    if (!waiting) {
      // Primer mensaje (o mensaje tras terminar): arranca desde el inicio.
      const start = nodes.find(n => n.type === "start");
      if (!start) {
        push({ from: "system", warn: true, text: "El flujo no tiene nodo de inicio." });
        return;
      }
      runIdRef.current += 1;
      varsRef.current = {};
      setVars({});
      return runFrom(start);
    }

    const node = waiting.node;
    const d = node.data || {};

    if (node.type === "input") {
      if (d.variableName) setVar(d.variableName, reply);
      const nxt = nextOf(node.id);
      if (!nxt) {
        setWaiting(null);
        push({ from: "system", text: "■ Fin del flujo" });
        onActiveNode && onActiveNode(null);
        return;
      }
      return runFrom(nxt);
    }

    // Menú: igual que el backend, primero por número y luego por texto.
    const opts = d.arrayOption || [];
    const num = reply.match(/(\d+)/);
    let opt = num ? opts.find(o => String(o.number) === num[1]) : null;
    if (!opt) {
      const pk = sanitize(reply);
      opt = opts.find(o => {
        const ov = sanitize(o.value);
        return ov && (pk === ov || pk.includes(ov));
      });
    }
    if (!opt) {
      push({ from: "system", warn: true, text: "Opción no reconocida: el contacto quedaría en este menú." });
      return;
    }
    const nxt = nextOf(node.id, `a${opt.number}`);
    if (!nxt) {
      setWaiting(null);
      push({ from: "system", warn: true, text: `La opción [${opt.number}] no está conectada a ningún nodo.` });
      return;
    }
    return runFrom(nxt);
  };

  const varEntries = Object.entries(vars);
  let lastOptionsIdx = -1;
  messages.forEach((m, i) => { if (m.options) lastOptionsIdx = i; });

  return (
    <Drawer anchor="right" open={open} onClose={onClose} variant="persistent" classes={{ paper: classes.paper }}>
      <div className={classes.header}>
        <ScienceIcon color="primary" />
        <Typography variant="subtitle1" style={{ flex: 1, fontWeight: 600 }}>
          Probar flujo (simulación)
        </Typography>
        <Tooltip title="Reiniciar">
          <IconButton size="small" onClick={reset}><ReplayIcon /></IconButton>
        </Tooltip>
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </div>
      <div className={classes.hint}>
        Nada se envía realmente. Escribe un mensaje como si fueras el contacto
        y verás cómo responde el flujo; el nodo en ejecución se resalta en el lienzo.
        Usa la versión actual del editor, aunque no esté guardada.
      </div>

      <div className={classes.chat} ref={chatRef}>
        {messages.length === 0 && (
          <div className={`${classes.bubble} ${classes.system}`}>
            Escribe un mensaje (por ejemplo «Hola») para iniciar el flujo
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={`${classes.bubble} ${classes[m.from]} ${m.warn ? classes.warn : ""}`}
          >
            {m.text}
            {m.options && m.options.length > 0 && waiting && i === lastOptionsIdx && (
              <div className={classes.options}>
                {m.options.map(o => (
                  <Chip
                    key={o.number}
                    size="small"
                    clickable
                    color="primary"
                    variant="outlined"
                    label={o.value || `Opción ${o.number}`}
                    onClick={() => answer(String(o.number))}
                  />
                ))}
              </div>
            )}
          </div>
        ))}
        {running && <div className={`${classes.bubble} ${classes.system}`}>…</div>}
      </div>

      {varEntries.length > 0 && (
        <div className={classes.vars}>
          <b>Variables:</b>{" "}
          {varEntries.map(([k, val]) => `${k} = ${val}`).join(" · ")}
        </div>
      )}

      <div className={classes.footer}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder={waiting ? "Responde como el contacto…" : "Mensaje del contacto…"}
          value={input}
          disabled={running}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter") answer(input);
          }}
          autoFocus
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => answer(input)}
          disabled={running || !input.trim()}
        >
          <SendIcon fontSize="small" />
        </Button>
      </div>
    </Drawer>
  );
};

export default FlowSimulatorDrawer;
